import React from 'react';
import styled from 'styled-components';

import { color, media } from 'styles/utils';

import Starfield from 'components/Starfield';
import Container from 'components/Container';
import Text from 'components/Text';
import Stack from 'components/Stack';
import Row from 'components/Row';

import IconStars from 'images/editorial-graphics/icon-stars.svg?svgr';
import IconRocket from 'images/editorial-graphics/icon-rocket.svg?svgr';
import IconSun from 'images/editorial-graphics/icon-sun.svg?svgr';
import IconGlobe from 'images/editorial-graphics/icon-globe.svg?svgr';

const Section = styled.section`
  background-color: ${color('darkestTeal')};
  color: ${color('white')};
  padding: 4.5rem 1rem;
  position: relative;
`;

const StyledStarfield = styled(Starfield)`
  mask-image: linear-gradient(#0000, #000 6rem, #000 calc(100% - 6rem), #0000);
  top: -4rem;
  bottom: -9rem;
`;

const Card = styled(Stack)`
  -webkit-font-smoothing: antialiased;
  background-image: radial-gradient(
      circle at center -50%,
      rgb(51 122 183 / 25%) 15%,
      rgb(51 122 183 / 0%) 75%
    ),
    linear-gradient(rgb(29 41 57 / 60%) 35%, rgb(16 24 40 / 60%) 100%);
  border-radius: 1rem;
  border: 0.125rem solid #344054;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-inline-size: 28rem;
  padding: 1.5rem 2rem 2rem;

  h3 {
    font-weight: bold;
    line-height: 1.35;
  }

  ${media.min('lg')} {
    h3 {
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      block-size: 3lh;
      display: -webkit-box;
      overflow: hidden;
    }
  }
`;

const Icon = styled('span')`
  background-color: #728293;
  border-radius: 100%;
  display: flex;
  padding: 1.25rem;
  width: fit-content;

  svg {
    height: 2.5rem;
    overflow: visible;
    width: 2.5rem;
  }
`;

const items = [
  {
    icon: <IconStars />,
    title: 'Focus on the User and the Rest Will Follow',
    description:
      'We are here for you in every way. First, our amazing tools help you achieve your goals. Then our teams go the extra mile to provide you with delightful experiences and superior services. Our fanatical focus on your success is sure to make you a fanatical Sleuth user.',
  },
  {
    icon: <IconRocket />,
    title: 'Choose Action',
    description:
      "We believe it's better to solve a problem quickly with an imperfect solution than to waste time striving for perfection. We encourage our team members to change what they think is broken. When in doubt: iterate, improve, then DO.",
  },
  {
    icon: <IconSun />,
    title: 'Be Open, All the Time',
    description:
      'Sleuth service and user experience teams are always available to you. We share information and collaborate freely to bring you the best solutions from our brightest minds.',
  },
  {
    icon: <IconGlobe />,
    title: 'Seek to Understand',
    description:
      'We seek to understand your needs — to build trust with you, and to provide you with the best services and experiences.',
  },
];

export default function OurValues() {
  return (
    <Section id="our-values">
      <StyledStarfield variant="secondary" />
      <Container>
        <Stack gap="2.5rem" align="center">
          <Text as="h2" variant="heading.section">
            Our Values
          </Text>
          <Container
            as={Row}
            colGap="1rem"
            rowGap="1rem"
            align="stretch"
            breakpoint="lg"
            layout="fixed"
          >
            {items.map(({ description, icon, title }) => (
              <Card key={title} gap="1.5rem">
                <Icon>{icon}</Icon>
                <Text as="h3" variant="lg">
                  {title}
                </Text>
                <Text variant="md">{description}</Text>
              </Card>
            ))}
          </Container>
        </Stack>
      </Container>
    </Section>
  );
}
